import React, { useCallback, useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { externalLoginStyles } from '../../helpers/loginStyles';
import OldUser from './OldUser';
import { startTimer } from '../../helpers/common';
import NewUser from './NewUser';
import ResetPassword from './ResetPassword';
import externalLoginService from '../../services/externalLoginService';
import { maxLength, noEscChar, otpRegex, required, strongPasswordRegex } from '../../helpers/commonYupValidations';
import { authLogin, authSelector } from '../../store/slices/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from 'reactstrap';

const ExternalLogin = () => {
    const dispatch = useDispatch()
    const authStatus = useSelector(authSelector);
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [userName, setUserName] = useState("");
    const [timerForOtp, setTimerForOtp] = useState("");
    const [emailId, setEmailId] = useState("");
    const encryptedEmailId = useSearchParams()[0].get('token');
    const [loginType, setLoginType] = useState({
        login: encryptedEmailId ? "new_login" : "old_login",
        step: 1,
        buttonType: encryptedEmailId ? "Set Password" : "Login"
    });

    //captcha states
    const [captchaText, setCaptchaText] = useState('');
    const [userInput, setUserInput] = useState('');
    const [counter, setCounter] = useState(0);
    const [isCapchaValidated, setIsCaptchaValidated] = useState(0);
    const [multipleLoginModalShow, setMultipleLoginModalShow] = useState(false);

    const canvasRef = useRef(null);

    // Initial values for different forms
    const initialValuesOld = {
        userName: '',
        password: '',
        otp: "",
    };

    const initialValuesNew = {
        userName: emailId,
        newPassword: "",
        confPassword: "",
        otp: ""
    };

    const initialValuesReset = {
        userName: '',
        newPassword: '',
        confPassword: '',
        otp: "",
    };

    // Validation schemas for different forms
    const validationSchemaOld = Yup.object().shape({
        userName: Yup.string().matches(noEscChar("Username").regex, noEscChar("Username").msg).max(150, maxLength('Username', 150).msg).required(required('Username').msg),
        password: Yup.string().matches(noEscChar("Password").regex, noEscChar("Password").msg).max(150, maxLength('Password', 150).msg).required(required('Password').msg),
        otp: loginType.step === 2 ? Yup.string().matches(noEscChar("Otp").regex, noEscChar("Otp").msg).required(required('Otp').msg).matches(otpRegex('Otp').regex, otpRegex('Otp').msg) : Yup.string().nullable().matches(noEscChar("Otp").regex, noEscChar("Otp").msg),
    });

    const validationSchemaNew = Yup.object().shape({
        userName: loginType.step === 1 ? Yup.string().matches(noEscChar("Username").regex, noEscChar("Username").msg).max(150, maxLength('Username', 150).msg).required(required('Username').msg) : Yup.string().nullable("").matches(noEscChar("Username").regex, noEscChar("Username").msg),
        newPassword: loginType.step === 1 ? Yup.string().matches(noEscChar("Password").regex, noEscChar("Password").msg).matches(strongPasswordRegex('Password').regex, strongPasswordRegex('Password').msg).required(required('Password').msg) : Yup.string().nullable().matches(noEscChar("Password").regex, noEscChar("Password").msg),
        confPassword: loginType.step === 1 ? Yup.string().oneOf([Yup.ref('newPassword'), null], 'Passwords must match').required(required('Confirm Password').msg).matches(noEscChar("Confirm Password").regex, noEscChar("Confirm Password").msg) : Yup.string().nullable().matches(noEscChar("Confirm Password").regex, noEscChar("Confirm Password").msg),
        otp: loginType.step === 2 ? Yup.string().matches(noEscChar("Otp").regex, noEscChar("Otp").msg).required(required('Otp').msg).matches(otpRegex('Otp').regex, otpRegex('Otp').msg) : Yup.string().nullable().matches(noEscChar("Otp").regex, noEscChar("Otp").msg),
    });

    const validationSchemaReset = Yup.object().shape({
        userName: loginType.step === 1 ? Yup.string().matches(noEscChar("Username").regex, noEscChar("Username").msg).max(150, maxLength('Username', 150).msg).required(required('Username').msg) : Yup.string().nullable().matches(noEscChar("Username").regex, noEscChar("Username").msg),
        newPassword: loginType.step === 3 ? Yup.string().matches(noEscChar("Password").regex, noEscChar("Password").msg).matches(strongPasswordRegex('Password').regex, strongPasswordRegex('Password').msg).required(required('Password').msg) : Yup.string().nullable().matches(noEscChar("Password").regex, noEscChar("Password").msg),
        confPassword: loginType.step === 3 ? Yup.string().oneOf([Yup.ref('newPassword'), null], 'Passwords must match').required(required('Confirm Password').msg).matches(noEscChar("Confirm Password").regex, noEscChar("Confirm Password").msg) : Yup.string().nullable().matches(noEscChar("Confirm Password").regex, noEscChar("Confirm Password").msg),
        otp: loginType.step === 2 ? Yup.string().matches(noEscChar("Otp").regex, noEscChar("Otp").msg).required(required('Otp').msg).matches(otpRegex('Otp').regex, otpRegex('Otp').msg) : Yup.string().nullable().matches(noEscChar("Otp").regex, noEscChar("Otp").msg),
    });

    const handleOldLogin = async (values) => {
        if (loginType.step === 1) {
            setIsLoading(true)
            const loginResponse = await externalLoginService.externalLogin({
                emailId: values.userName,
                password: values.password,
                loginType: "External"
            });
            setIsLoading(false)
            if (loginResponse?.data?.isSuccess) {
                startTimer(setTimerForOtp);
                setLoginType({ ...loginType, step: 2, buttonType: "Login" });
                toast.success(loginResponse?.data.message);
            } else {
                toast.error(loginResponse?.data.message);
                initializeCaptcha(canvasRef.current.getContext('2d'))
            }
        } else {
            let otpVerifyPayload = {
                emailId: values.userName,
                otp: values.otp,
                purpose: "login"
            }
            setIsLoading(true)
            const verifyOtpResponse = await externalLoginService.verifyOtp(otpVerifyPayload);
            setIsLoading(false);
            if (verifyOtpResponse?.data?.statusCode === 409) {
                setUserName(values?.userName)
                localStorage.setItem('token', verifyOtpResponse?.data?.jwtToken);
                setMultipleLoginModalShow(true);
            } else {
                if (verifyOtpResponse?.data?.isSuccess) {
                    let login_data = verifyOtpResponse.data.data;
                    dispatch(authLogin({ ...login_data }));
                    localStorage.setItem('token', verifyOtpResponse?.data?.jwtToken);
                    localStorage.removeItem("resendOtpOldLoginCount")
                    navigate('/external/dashboard');
                    toast.success(verifyOtpResponse?.data.message);
                }
                else {
                    toast.error(verifyOtpResponse?.response.data.message);
                    initializeCaptcha(canvasRef.current.getContext('2d'))
                }
            }

        }
    };


    const submitForceLogin = async () => {
        let payload = {
            emailId: userName
        }
        setIsLoading(true)
        const verifyOtpResponse = await externalLoginService.forceLogin(payload);
        setIsLoading(false);
        if (verifyOtpResponse?.data?.isSuccess) {
            let login_data = verifyOtpResponse.data.data;
            dispatch(authLogin({ ...login_data }));
            localStorage.setItem('token', verifyOtpResponse?.data?.jwtToken);
            navigate('/external/dashboard');
            toast.success(verifyOtpResponse?.data.message);
        }
        else {
            toast.error(verifyOtpResponse?.response.data.message);
        }
    }

    const closeMultipleLoginModal = () => {
        setMultipleLoginModalShow(false);
    }

    const handleCheckLogin = async () => {
        if (authStatus?.token === null && !authStatus?.isAuthenticated) {
            sessionStorage.clear();
            localStorage.clear();
            localStorage.clear();
            toast.error('Your session is expired');
            navigate('/ExternalLogin');
        } else {
            navigate('/External/dashboard');
        }
    };

    const handleNewLogin = async (values) => {
        if (loginType.step === 1) {
            setIsLoading(true)
            const setPasswordResponse = await externalLoginService.setPassowrd({
                emailId: emailId,
                password: values.newPassword,
                confirmPassword: values.confPassword
            });
            setIsLoading(false)
            if (setPasswordResponse?.data?.isSuccess) {
                startTimer(setTimerForOtp);
                setLoginType({ ...loginType, step: 2, buttonType: "Verify OTP" });
                toast.success(setPasswordResponse.data.message);
            } else {
                toast.error(setPasswordResponse.response.data.message);
                initializeCaptcha(canvasRef.current.getContext('2d'))
            }
        } else {
            let otpVerifyPayload = {
                emailId: emailId,
                otp: values.otp,
                purpose: "set-password"
            }
            setIsLoading(true)
            const verifyOtpResponse = await externalLoginService.verifyOtp(otpVerifyPayload);
            setIsLoading(false);
            if (verifyOtpResponse?.data?.isSuccess) {
                localStorage.removeItem("resendOtpNewLoginCount")
                setLoginType({ ...loginType, step: 1, buttonType: "Login", login: "old_login" });
                toast.success(verifyOtpResponse?.data.message);
            } else {
                toast.error(verifyOtpResponse?.response.data.message);
                initializeCaptcha(canvasRef.current.getContext('2d'))
            }
        }
    };

    const handleResetLogin = async (values) => {
        if (loginType.step === 1) {
            let resetPayload = {
                emailId: values.userName
            }
            setIsLoading(true)
            const resetPasswordResponse = await externalLoginService.forgotPassword(resetPayload);
            setIsLoading(false);
            if (resetPasswordResponse?.data?.isSuccess) {
                startTimer(setTimerForOtp);
                setLoginType({ ...loginType, step: 2, buttonType: "Verify OTP" });
                toast.success(resetPasswordResponse?.data.message);
            } else {
                toast.error(resetPasswordResponse?.data.message);
                initializeCaptcha(canvasRef.current.getContext('2d'))
            }

        } else if (loginType.step === 2) {
            let otpVerifyPayload = {
                emailId: values.userName,
                otp: values.otp,
                purpose: "reset-password"
            }
            setIsLoading(true)
            const verifyOtpResponse = await externalLoginService.verifyOtp(otpVerifyPayload);
            setIsLoading(false);
            if (verifyOtpResponse?.data?.isSuccess) {
                startTimer(setTimerForOtp);
                setLoginType({ ...loginType, step: 3, buttonType: "Set Password" });
                toast.success(verifyOtpResponse?.data.message);
            } else {
                toast.error(verifyOtpResponse.response.data.message);
                initializeCaptcha(canvasRef.current.getContext('2d'))
            }
        } else {
            let resetPayload = {
                emailId: values.userName,
                password: values.newPassword,
                confirmPassword: values.confPassword
            }
            setIsLoading(true)
            const resetPasswordResponse = await externalLoginService.resetPassword(resetPayload);
            setIsLoading(false);
            if (resetPasswordResponse?.data?.isSuccess) {
                localStorage.removeItem("resendOtpResetCount")
                setLoginType({ ...loginType, step: 1, buttonType: "Login", login: "old_login" });
                toast.success(resetPasswordResponse?.data.message);
            } else {
                toast.error(resetPasswordResponse?.response.data.message);
                initializeCaptcha(canvasRef.current.getContext('2d'))
            }
        }
    };

    const fetchNewUser = useCallback(async () => {
        if (loginType.login === "new_login") {
            setIsLoading(true);
            const verifyEmailResponse = await externalLoginService.verifyUserEmail(encodeURIComponent(encryptedEmailId));
            if (verifyEmailResponse?.data?.isSuccess) {
                setEmailId(verifyEmailResponse?.data?.emailId)
                toast.success(verifyEmailResponse?.data?.message);
            } else {
                toast.error(verifyEmailResponse?.data.message);
                if (verifyEmailResponse?.data.message === "Your account is already registered. Please login." || verifyEmailResponse?.data.message === "Your account is currently locked. Please reset your password to get access by clicking forgot password button.") {
                    setLoginType({ ...loginType, step: 1, buttonType: "Login", login: "old_login" });
                    navigate("/externalLogin")
                }
            }
            setIsLoading(false);
        }
    }, []);

    //captcha
    const generateRandomChar = (min, max) =>
        String.fromCharCode(Math.floor
            (Math.random() * (max - min + 1) + min));

    const generateCaptchaText = () => {
        let captcha = '';
        for (let i = 0; i < 2; i++) {
            captcha += generateRandomChar(65, 90);
            captcha += generateRandomChar(97, 122);
            captcha += generateRandomChar(48, 57);
        }
        return captcha.split('').sort(
            () => Math.random() - 0.5).join('');
    };

    const drawCaptchaOnCanvas = (ctx, captcha) => {
        ctx?.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        const letterSpace = 150 / captcha.length;
        for (let i = 0; i < captcha.length; i++) {
            const xInitialSpace = 20;
            ctx.font = '20px Roboto Mono';
            ctx.fillText(
                captcha[i],
                xInitialSpace + i * letterSpace,
                // Randomize Y position slightly
                Math.floor(Math.random() * 16 + 25),
                100
            );
        }
    };

    useEffect(() => {
        window.scroll(0, 0);
        const fetchUser = async () => {
            await fetchNewUser();
        };
        fetchUser();
    }, [fetchNewUser]);

    //captcha 
    const initializeCaptcha = (ctx) => {
        setUserInput('');
        const newCaptcha = generateCaptchaText();
        setCaptchaText(newCaptcha);
        drawCaptchaOnCanvas(ctx, newCaptcha);
        setCounter(0);
        setIsCaptchaValidated(false);
    };

    const handleUserInputChange = (e) => {
        setUserInput(e.target.value);
    };

    const handleCaptchaSubmit = (values) => {
        if (userInput === captchaText) {
            setIsCaptchaValidated(2);
            if (loginType.login === "old_login") {
                handleOldLogin(values);
            } else if (loginType.login === "new_login") {
                handleNewLogin(values);
            } else {
                handleResetLogin(values);
            }
        }
        else {
            setIsCaptchaValidated(1);
            setCounter((counter) => counter + 1);
        }
    };

    useEffect(() => {
        if (counter === 0 || counter >= 8) {
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext('2d');
            initializeCaptcha(ctx);
        }
    }, [counter]);

    return (
        <div className="login_page" style={externalLoginStyles.login_page}>
            <div>
                <div className="text-center">
                    <h2 style={externalLoginStyles.login_title}>Insurance Management System</h2>
                </div>
                {authStatus?.token === null && !authStatus?.isAuthenticated ? (

                    <div className="login_area" style={externalLoginStyles.login_area}>
                        <div>
                            <Formik
                                enableReinitialize
                                initialValues={
                                    loginType.login === "old_login" ? initialValuesOld : loginType.login === "new_login" ? initialValuesNew : initialValuesReset
                                }
                                validationSchema={
                                    loginType.login === "old_login" ? validationSchemaOld : loginType.login === "new_login" ? validationSchemaNew : validationSchemaReset
                                }
                                onSubmit={
                                    handleCaptchaSubmit
                                }
                            >
                                {({ touched, errors, dirty, values }) => (
                                    <Form role="form">
                                        {loginType.login === "old_login" ?
                                            <OldUser
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                loginType={loginType}
                                                timerForOtp={timerForOtp}
                                                setTimerForOtp={setTimerForOtp}
                                            />
                                            : loginType.login === "new_login" ?
                                                <NewUser
                                                    values={values}
                                                    touched={touched}
                                                    errors={errors}
                                                    loginType={loginType}
                                                    timerForOtp={timerForOtp}
                                                    setTimerForOtp={setTimerForOtp}
                                                />
                                                : loginType.login === "reset" ?
                                                    <ResetPassword
                                                        values={values}
                                                        touched={touched}
                                                        errors={errors}
                                                        loginType={loginType}
                                                        timerForOtp={timerForOtp}
                                                        setTimerForOtp={setTimerForOtp}
                                                    />
                                                    : null}
                                        <div className="wrapper">
                                            <canvas ref={canvasRef}
                                                width="200"
                                                height="70">
                                            </canvas>
                                            <div className='d-flex align-items-center'>
                                                {isCapchaValidated === 2 ? '' : <Button
                                                    color="link"
                                                    className="ms-1 me-3 p-0"
                                                    style={{ fontSize: "25px" }}
                                                    onClick={
                                                        () => initializeCaptcha(
                                                            canvasRef.current.getContext('2d'))}
                                                >
                                                    <i className="bx bx-refresh"></i>
                                                </Button>}
                                                <div>
                                                    <input
                                                        type="text"
                                                        id="user-input"
                                                        placeholder="Enter image text"
                                                        value={userInput}
                                                        className={`form-control ${isCapchaValidated === 1 ? 'is-invalid' : ''}`}
                                                        onChange={handleUserInputChange}
                                                        disabled={isCapchaValidated === 2}
                                                    />

                                                    {isCapchaValidated === 1 ? (
                                                        <p className="errorMsg">Invaid Captcha. {`${8 - counter} tries left`}</p>) : null}

                                                    {isCapchaValidated === 2 ? <p style={{ float: "right", display: "flex", alignItems: "center", color: "green" }}><i class="bx bx-check-circle me-1" ></i>Verified</p> : ''}
                                                </div>
                                            </div>
                                        </div>
                                        {!isLoading && dirty ? (
                                            <button type="submit" className="login_button mt-4" style={externalLoginStyles.login_button} disabled={!dirty || isLoading}>
                                                {loginType.buttonType}
                                            </button>
                                        ) : (
                                            <button type="button" className="login_button" style={externalLoginStyles.login_button_disabled} disabled>
                                                {loginType.buttonType}
                                            </button>
                                        )}
                                    </Form>
                                )}
                            </Formik>
                        </div>
                        <div className='forgot_password'>
                            <button className='mt-2'
                                onClick={() => setLoginType({ ...loginType, step: 1, login: "reset", buttonType: "Verify User" })}>
                                Forgot Password
                            </button>
                        </div>

                    </div>
                )
                    : (
                        <div>
                            <h2 style={externalLoginStyles.login_subtitle}>You have successfully logged in to the dashboard.</h2>
                            <div className="login_area" style={externalLoginStyles.login_area}>
                                <button className="login_button" style={externalLoginStyles.login_button} onClick={handleCheckLogin}>Go to dashboard</button>
                            </div>
                        </div>
                    )}
            </div>
            <Modal isOpen={multipleLoginModalShow}>
                <ModalHeader toggle={closeMultipleLoginModal}>Login</ModalHeader>
                <ModalBody>You are already logged in on another device. Do you want to log out from other devices?
                </ModalBody>
                <ModalFooter>
                    <Button onClick={() => {
                        submitForceLogin()
                    }}>Yes</Button>
                    <Button onClick={closeMultipleLoginModal}>Cancel</Button>
                </ModalFooter>
            </Modal>
        </div>
    )
}

export default ExternalLogin;
